import { useState } from "react";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { DateField } from "@/components/common/masked-form-field";
import {
  portalOtherAssets,
  portalProperties,
  portalVehicles,
  type PortalCollectionApi,
} from "@/features/portal/api";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import {
  CollectionCard,
  type Column,
  type PortalRecord,
  usePortalCollection,
} from "./collection-card";
import { PortalCrudDialog } from "./crud-dialog";
import { CheckboxInput, MoneyInput, SelectInput } from "./fields";
import { isoToMaskedDate, maskedDateToISO } from "@/lib/masks";
import { bool, dash, money, num, str } from "./record";

const nonNegMoney = Yup.string().test(
  "non-negative",
  "Must be zero or more",
  (v) => !v || dollarsToCents(v) >= 0,
);
const centsInit = (r: PortalRecord | null, key: string) =>
  centsToDollars(num(r ?? {}, key) ?? 0);

// --- Properties --------------------------------------------------------------

interface PropertyValues {
  description: string;
  title: string;
  address: string;
  current_value: string;
  loan_balance: string;
  monthly_pmt: string;
  final_pmt_date: string;
  lender: string;
  lender_contact: string;
  income_gen: boolean;
  rent_exp: string;
}

function PropertyDialog({
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const r = record ?? {};
  const initial: PropertyValues = {
    description: str(r, "description"),
    title: str(r, "title"),
    address: str(r, "address"),
    current_value: centsInit(record, "current_value"),
    loan_balance: centsInit(record, "loan_balance"),
    monthly_pmt: centsInit(record, "monthly_pmt"),
    final_pmt_date: isoToMaskedDate(str(r, "final_pmt_date")),
    lender: str(r, "lender"),
    lender_contact: str(r, "lender_contact"),
    income_gen: bool(r, "income_gen"),
    rent_exp: centsInit(record, "rent_exp"),
  };

  async function onSave(v: PropertyValues) {
    const payload = {
      description: v.description,
      title: v.title,
      address: v.address,
      current_value: dollarsToCents(v.current_value),
      loan_balance: dollarsToCents(v.loan_balance),
      monthly_pmt: dollarsToCents(v.monthly_pmt),
      final_pmt_date: v.final_pmt_date ? maskedDateToISO(v.final_pmt_date) || null : null,
      lender: v.lender,
      lender_contact: v.lender_contact,
      income_gen: v.income_gen,
      rent_exp: dollarsToCents(v.rent_exp),
    };
    if (editing && record) await portalProperties.update(record.id, payload);
    else await portalProperties.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Property"
      className="sm:max-w-2xl"
      initialValues={initial}
      validationSchema={Yup.object({
        description: Yup.string().trim().required("Required"),
        current_value: nonNegMoney,
        loan_balance: nonNegMoney,
        monthly_pmt: nonNegMoney,
        rent_exp: nonNegMoney,
      })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="description" label="Description" />
      <div className="grid grid-cols-2 gap-4">
        <FormField name="title" label="Held in the name of" />
        <FormField name="address" label="Address" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <MoneyInput name="current_value" label="Current value" />
        <MoneyInput name="loan_balance" label="Loan balance" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <MoneyInput name="monthly_pmt" label="Monthly payment" />
        <DateField name="final_pmt_date" label="Final payment date" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField name="lender" label="Lender" />
        <FormField name="lender_contact" label="Lender contact" />
      </div>
      <CheckboxInput name="income_gen" label="Generates income" />
      <MoneyInput name="rent_exp" label="Monthly rental expense" />
    </PortalCrudDialog>
  );
}

function PropertiesSection() {
  const { rows, loading, reload } = usePortalCollection(
    portalProperties as PortalCollectionApi<PortalRecord>,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });
  const columns: Column<PortalRecord>[] = [
    { header: "Description", cell: (r) => dash(str(r, "description")) },
    { header: "Address", cell: (r) => dash(str(r, "address")) },
    { header: "Value", cell: (r) => money(r, "current_value") },
    { header: "Loan balance", cell: (r) => money(r, "loan_balance") },
  ];
  return (
    <>
      <CollectionCard
        title="Real estate"
        addLabel="Add Property"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalProperties.remove(row.id)}
        onDeleted={reload}
      />
      <PropertyDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

// --- Vehicles ----------------------------------------------------------------

interface VehicleValues {
  description: string;
  title: string;
  year: string;
  make: string;
  model: string;
  current_value: string;
  loan_balance: string;
  lease_loan: string;
  income_gen: boolean;
}

const LEASE_LOAN_OPTIONS = [
  { value: "OWN", label: "Own" },
  { value: "LEASE", label: "Lease" },
];

function VehicleDialog({
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const r = record ?? {};
  const initial: VehicleValues = {
    description: str(r, "description"),
    title: str(r, "title"),
    year: num(r, "year")?.toString() ?? "",
    make: str(r, "make"),
    model: str(r, "model"),
    current_value: centsInit(record, "current_value"),
    loan_balance: centsInit(record, "loan_balance"),
    lease_loan: str(r, "lease_loan") || "OWN",
    income_gen: bool(r, "income_gen"),
  };

  async function onSave(v: VehicleValues) {
    const payload = {
      description: v.description,
      title: v.title,
      year: v.year === "" ? null : Number(v.year),
      make: v.make,
      model: v.model,
      current_value: dollarsToCents(v.current_value),
      loan_balance: dollarsToCents(v.loan_balance),
      lease_loan: v.lease_loan,
      income_gen: v.income_gen,
    };
    if (editing && record) await portalVehicles.update(record.id, payload);
    else await portalVehicles.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Vehicle"
      className="sm:max-w-2xl"
      initialValues={initial}
      validationSchema={Yup.object({
        current_value: nonNegMoney,
        loan_balance: nonNegMoney,
        year: Yup.number().typeError("Enter a year").nullable(),
      })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="description" label="Description" />
      <div className="grid grid-cols-3 gap-4">
        <FormField name="year" label="Year" inputMode="numeric" />
        <FormField name="make" label="Make" />
        <FormField name="model" label="Model" />
      </div>
      <FormField name="title" label="Held in the name of" />
      <div className="grid grid-cols-2 gap-4">
        <MoneyInput name="current_value" label="Current value" />
        <MoneyInput name="loan_balance" label="Loan balance" />
      </div>
      <SelectInput name="lease_loan" label="Own or lease" options={LEASE_LOAN_OPTIONS} />
      <CheckboxInput name="income_gen" label="Generates income" />
    </PortalCrudDialog>
  );
}

function VehiclesSection() {
  const { rows, loading, reload } = usePortalCollection(
    portalVehicles as PortalCollectionApi<PortalRecord>,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });
  const columns: Column<PortalRecord>[] = [
    {
      header: "Vehicle",
      cell: (r) =>
        dash([str(r, "year"), str(r, "make"), str(r, "model")].filter(Boolean).join(" ")),
    },
    { header: "Value", cell: (r) => money(r, "current_value") },
    { header: "Loan balance", cell: (r) => money(r, "loan_balance") },
    { header: "Own/Lease", cell: (r) => dash(str(r, "lease_loan")) },
  ];
  return (
    <>
      <CollectionCard
        title="Vehicles"
        addLabel="Add Vehicle"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalVehicles.remove(row.id)}
        onDeleted={reload}
      />
      <VehicleDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

// --- Other assets ------------------------------------------------------------

interface OtherAssetValues {
  description: string;
  title: string;
  current_value: string;
  loan_balance: string;
}

function OtherAssetDialog({
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const r = record ?? {};
  const initial: OtherAssetValues = {
    description: str(r, "description"),
    title: str(r, "title"),
    current_value: centsInit(record, "current_value"),
    loan_balance: centsInit(record, "loan_balance"),
  };

  async function onSave(v: OtherAssetValues) {
    const payload = {
      description: v.description,
      title: v.title,
      current_value: dollarsToCents(v.current_value),
      loan_balance: dollarsToCents(v.loan_balance),
    };
    if (editing && record) await portalOtherAssets.update(record.id, payload);
    else await portalOtherAssets.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Asset"
      initialValues={initial}
      validationSchema={Yup.object({
        description: Yup.string().trim().required("Required"),
        current_value: nonNegMoney,
        loan_balance: nonNegMoney,
      })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="description" label="Description" />
      <FormField name="title" label="Held in the name of" />
      <div className="grid grid-cols-2 gap-4">
        <MoneyInput name="current_value" label="Current value" />
        <MoneyInput name="loan_balance" label="Loan balance" />
      </div>
    </PortalCrudDialog>
  );
}

function OtherAssetsSection() {
  const { rows, loading, reload } = usePortalCollection(
    portalOtherAssets as PortalCollectionApi<PortalRecord>,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });
  const columns: Column<PortalRecord>[] = [
    { header: "Description", cell: (r) => dash(str(r, "description")) },
    { header: "Value", cell: (r) => money(r, "current_value") },
    { header: "Loan balance", cell: (r) => money(r, "loan_balance") },
  ];
  return (
    <>
      <CollectionCard
        title="Other assets"
        addLabel="Add Asset"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalOtherAssets.remove(row.id)}
        onDeleted={reload}
      />
      <OtherAssetDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

export function AssetsTab() {
  return (
    <div className="grid gap-6">
      <PropertiesSection />
      <VehiclesSection />
      <OtherAssetsSection />
    </div>
  );
}
